import { Link } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useLocale } from "@admin/app/locale";
import { LoadingSkeleton, ErrorState } from "@a2z/ui";

export function DashboardPage() {
  const t = useTranslations();
  const locale = useLocale();
  const { data: session, isLoading, error, refetch } = useSessionQuery();
  const isSuperAdmin =
    session?.admin?.roles?.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;

  if (isLoading) {
    return <LoadingSkeleton variant="card" rows={3} />;
  }

  if (error || !session) {
    return <ErrorState onRetry={refetch} />;
  }

  const tiles = [
    {
      href: `/${locale}/admin/sellers`,
      label: t(keys.admin.sellersTitle),
      icon: <SellersIcon />,
    },
    {
      href: `/${locale}/admin/stores`,
      label: t(keys.admin.storesTitle),
      icon: <StoresIcon />,
    },
    ...(isSuperAdmin
      ? [
          {
            href: `/${locale}/admin/admins`,
            label: t(keys.admin.adminsTitle),
            icon: <AdminsIcon />,
          },
        ]
      : []),
  ];

  return (
    <section className="flex w-full flex-col gap-5 lg:px-6 lg:py-6">
      <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
        {t(keys.admin.dashboardTitle)}
      </h1>

      <div className="grid grid-cols-2 gap-3.5 sm:[grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
        {tiles.map((tile) => (
          <Link
            key={tile.href}
            to={tile.href}
            className="group flex items-center gap-3 rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-4 text-start shadow-[0_1px_2px_rgba(15,20,40,0.04)] transition-colors hover:bg-[color:var(--surface-alt)]"
          >
            <span
              aria-hidden="true"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-primary)]"
            >
              {tile.icon}
            </span>
            <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-[color:var(--text-primary)]">
              {tile.label}
            </span>
            <ChevronEnd />
          </Link>
        ))}
      </div>
    </section>
  );
}

const SVG_PROPS = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function SellersIcon() {
  return (
    <svg {...SVG_PROPS}>
      <circle cx="9" cy="7" r="4" />
      <path d="M2 21a7 7 0 0 1 14 0" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      <path d="M22 21a7 7 0 0 0-5-6.71" />
    </svg>
  );
}

function StoresIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M3 9 4 4h16l1 5" />
      <path d="M4 9v11h16V9" />
      <path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" />
      <path d="M9 20v-5h6v5" />
    </svg>
  );
}

function AdminsIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M12 3 4 6v6c0 5 3.5 7.5 8 9 4.5-1.5 8-4 8-9V6z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function ChevronEnd() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-[color:var(--text-muted)] rtl:rotate-180"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
